import env from 'enviro';
import http from 'hub-http/clients/apiClient';
import { Fragment } from 'react';
import { KOALA, BATTLESHIP } from 'HubStyleTokens/colors';
import UIIconCircle from 'UIComponents/icon/UIIconCircle';
import UIMediaObject from 'UIComponents/layout/UIMediaObject';
import UITile from 'UIComponents/tile/UITile';
import UITileSection from 'UIComponents/tile/UITileSection';
import { getAppResponseUrl } from './SyncBridge';
import { getDataSyncAuthConfigUrl, getUnifiedAuthConfigUrl } from './Api';
import { AuthenticateError, DuplicateDataSyncAccountError, MissingPermissions, UnifiedAuthView } from './LowLevelUnifiedAuth';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UIButton from 'UIComponents/button/UIButton';
import UIStepIndicator from 'UIComponents/connectedStep/UIStepIndicator';
import UIDialogBody from 'UIComponents/dialog/UIDialogBody';
import Small from 'UIComponents/elements/Small';
import UIButtonWrapper from 'UIComponents/layout/UIButtonWrapper';
import UIFlex from 'UIComponents/layout/UIFlex';
import { UniversalAuthBeamTracker } from './UsageTracker';
import { hubletApi, resolveApi } from 'hub-http/middlewares/core';
import { buildUrl } from 'hub-http/helpers/url';
import UIDialogCloseButton from 'UIComponents/dialog/UIDialogCloseButton';
import UIDialogHeader from 'UIComponents/dialog/UIDialogHeader';
import { H2 } from 'UIComponents/elements/headings';
import UIDialogHeaderImage from 'UIComponents/dialog/UIDialogHeaderImage';
import UIIllustration from 'UIComponents/image/UIIllustration';
import { View } from 'foundations-components/transitional/components/View';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const googleAppId = env.isQa() ? 1203680 : 232794;
export function fetchAuthConfigForDataSync(portalId, appId, accountId, hubletOverride) {
  return async () => {
    const appResponse = await http.get(buildUrl({
      hostname: resolveApi(hubletApi('app', 'hubspot', hubletOverride)),
      path: getAppResponseUrl(appId)
    }));
    const app = appResponse.applicationListing;
    const usesUnifiedAuth = !!appResponse.authInfo;
    const authFlow = await http.get(buildUrl({
      hostname: resolveApi(hubletApi('app', 'hubspot', hubletOverride)),
      path: usesUnifiedAuth ? getUnifiedAuthConfigUrl(appResponse.authInfo.authenticatorId, app.name) : getDataSyncAuthConfigUrl(appId)
    }));
    return {
      appName: app.name,
      appIcon: app.iconUrl || app.extensionIconUrl || '',
      oAuthScope: appResponse.authInfo ? appResponse.authInfo.scope : undefined,
      authFlow,
      makeAuthorizeUrlDescriptor() {
        return usesUnifiedAuth ? {
          hostname: resolveApi(hubletApi('app', 'hubspot', hubletOverride)),
          path: `/api/unified-auth/v1/authenticators/${appResponse.authInfo.authenticatorId}/authorize`
        } : {
          hostname: resolveApi(hubletApi('sync', 'hubspot', hubletOverride)),
          path: `/${appResponse.connectorName}/credentials/authorize/hubspot`
        };
      },
      finishAuthFlow(payload) {
        // Google is updating the UI of their OAuth popup, resulting in the contacts permissions being unchecked by default.
        // If a user does not tick the contacts checkbox, Data Sync will proceed to store the user's Google account lacking sufficient permissions.
        // Without the contact permissions, we won’t be able to sync.
        // To prevent this, we are checking if the user has not ticked the contacts checkbox through the granted scopes and throwing an error.
        if (appId === googleAppId && payload.upstreamParams.scope && !payload.upstreamParams.scope.includes('https://www.googleapis.com/auth/contacts')) {
          return Promise.reject(new MissingPermissions());
        }
        if (usesUnifiedAuth) {
          const authenticatorId = appResponse.authInfo.authenticatorId;

          // step 1: obtain `secretId` from Unified Auth
          return http.post(buildUrl({
            hostname: resolveApi(hubletApi('app', 'hubspot', hubletOverride)),
            path: `/api/unified-auth/v1/authenticators/${authenticatorId}/secret`
          }), {
            data: payload
          }).then(finishPayload => {
            if (typeof finishPayload === 'object' && 'status' in finishPayload && finishPayload.status === 'error') {
              throw new AuthenticateError(finishPayload.message, finishPayload.upstreamErrorMessage);
            } else {
              return finishPayload;
            }
          })
          // step 2: use `secretId` to create an account through the Sync Bridge
          .then(({
            secretId
          }) => {
            return http.post(buildUrl({
              hostname: resolveApi(hubletApi('app', 'hubspot', hubletOverride)),
              path: accountId ? `/api/sync-bridge/v4/accounts/${accountId}/credentials` : '/api/sync-bridge/v4/install/finish'
            }), {
              query: {
                portalId,
                appId,
                secretId
              }
            });
          }).catch(err => {
            if (err.responseJSON && err.responseJSON.category && err.responseJSON.category === 'OBJECT_ALREADY_EXISTS' && err.responseJSON.context) {
              const {
                context
              } = err.responseJSON;
              const duplicateAccount = {
                id: context.id[0],
                accountName: context.accountName[0],
                accountIdentifier: context.accountIdentifier[0],
                connectionIds: []
              };
              throw new DuplicateDataSyncAccountError(portalId, appId, duplicateAccount);
            }
            throw err;
          });
        }

        // Legacy flow
        return http.post(buildUrl({
          hostname: resolveApi(hubletApi('app', 'hubspot', hubletOverride)),
          path: accountId ? `/api/sync-bridge/v3/accounts/${accountId}/credentials` : '/api/sync-bridge/v3/install/finish'
        }), {
          data: payload,
          query: {
            portalId,
            appId: appId.toString()
          }
        }).then(account => account).catch(err => {
          if (err.responseJSON && err.responseJSON.category && err.responseJSON.category === 'OBJECT_ALREADY_EXISTS' && err.responseJSON.context) {
            const {
              context
            } = err.responseJSON;
            const duplicateAccount = {
              id: context.id[0],
              accountName: context.accountName[0],
              accountIdentifier: context.accountIdentifier[0],
              connectionIds: []
            };
            throw new DuplicateDataSyncAccountError(portalId, appId, duplicateAccount);
          }
          throw new AuthenticateError(undefined, err.responseJSON.message);
        });
      }
    };
  };
}
export function DataSyncIntroScreen({
  appName
}) {
  return /*#__PURE__*/_jsx(UIFlex, {
    "data-test-id": "data-sync-intro",
    children: /*#__PURE__*/_jsx(UIStepIndicator, {
      direction: "vertical",
      stepIndex: 0,
      stepNames: [/*#__PURE__*/_jsxs(Fragment, {
        children: [/*#__PURE__*/_jsx("p", {
          style: {
            fontSize: '14px',
            textTransform: 'none',
            fontWeight: 600
          },
          className: "m-bottom-2",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "universalAuth.connect",
            options: {
              appName
            }
          })
        }), /*#__PURE__*/_jsx(Small, {
          use: "help",
          tagName: "p",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "universalAuth.auth.connectDescription",
            options: {
              appName
            }
          })
        })]
      }, 0), /*#__PURE__*/_jsxs(Fragment, {
        children: [/*#__PURE__*/_jsx("p", {
          style: {
            fontSize: '14px',
            textTransform: 'none',
            fontWeight: 600
          },
          className: "m-bottom-2",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "universalAuth.dataSync.step.title"
          })
        }), /*#__PURE__*/_jsx(Small, {
          use: "help",
          tagName: "p",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "universalAuth.dataSync.step.description"
          })
        })]
      }, 1)]
    })
  });
}
export function DataSyncSuccessScreen({
  portalId,
  appId,
  appName,
  account,
  onOpenExternalLink,
  onSetupSyncClick
}) {
  return /*#__PURE__*/_jsxs(UIDialogBody, {
    children: [/*#__PURE__*/_jsx(UIFlex, {
      children: /*#__PURE__*/_jsx(UIStepIndicator, {
        direction: "vertical",
        stepIndex: 1,
        stepNames: [/*#__PURE__*/_jsxs(Fragment, {
          children: [/*#__PURE__*/_jsx("p", {
            style: {
              fontSize: '14px',
              textTransform: 'none',
              fontWeight: 600
            },
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "universalAuth.connect",
              options: {
                appName
              }
            })
          }), /*#__PURE__*/_jsx(UIMediaObject, {
            "data-test-id": "data-sync-account-avatar",
            className: "p-x-6",
            itemLeft: /*#__PURE__*/_jsx(UIIconCircle, {
              name: "contacts",
              color: BATTLESHIP,
              backgroundColor: KOALA,
              borderWidth: 0,
              size: 16
            }),
            children: /*#__PURE__*/_jsx("div", {
              className: "justify-between",
              children: /*#__PURE__*/_jsxs("div", {
                children: [/*#__PURE__*/_jsx("div", {
                  style: {
                    textTransform: 'none'
                  },
                  children: account.accountName
                }), account.accountIdentifier !== '' && /*#__PURE__*/_jsx("div", {
                  children: /*#__PURE__*/_jsx(Small, {
                    use: "help",
                    children: account.accountIdentifier
                  })
                })]
              })
            })
          })]
        }, 0), /*#__PURE__*/_jsxs(Fragment, {
          children: [/*#__PURE__*/_jsx("p", {
            style: {
              fontSize: '14px',
              textTransform: 'none',
              fontWeight: 600
            },
            className: "m-bottom-2",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "universalAuth.dataSync.step.title"
            })
          }), /*#__PURE__*/_jsx(Small, {
            use: "help",
            tagName: "p",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "universalAuth.dataSync.step.description"
            })
          })]
        }, 1)]
      })
    }), /*#__PURE__*/_jsx(View, {
      paddingBlock: "600",
      as: "footer",
      children: /*#__PURE__*/_jsx(UIButtonWrapper, {
        justify: "center",
        children: /*#__PURE__*/_jsx(UIButton, {
          "data-test-id": "continue-setup-button",
          use: "primary",
          href: `/data-sync/${portalId}/app/${appId}/account/${account.id}`,
          onClick: event => {
            UniversalAuthBeamTracker.track('sync-setup', {
              applicationId: appId,
              applicationName: appName,
              action: 'START_SYNC_SETUP'
            });
            if (onSetupSyncClick) {
              // @ts-expect-error bug in UIComponents: https://hubspot.slack.com/archives/C1WG3L3J6/p1645533761109989
              onSetupSyncClick(event);
            }
            if (onOpenExternalLink) {
              onOpenExternalLink(event);
            }
          },
          responsive: false,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "universalAuth.dataSync.continueSetup"
          })
        })
      })
    })]
  });
}
export function DataSyncReauthForm({
  portalId,
  appId,
  appName,
  account,
  onComplete
}) {
  return /*#__PURE__*/_jsx(UnifiedAuthView, {
    isModal: false,
    isReauth: true,
    portalId: portalId,
    fetchAuthConfig: fetchAuthConfigForDataSync(portalId, appId, account.id, undefined /* don't override the hublet */),
    onComplete: onComplete,
    onCancel: () => {},
    introScreen: ({
      type
    }) => type === 'OAuth' ? /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(AccountTile, {
        account: account
      }), /*#__PURE__*/_jsx(View, {
        marginBlockStart: "200",
        as: "div",
        children: /*#__PURE__*/_jsx(Small, {
          children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
            message: "universalAuth.dataSync.reauthenticate.oAuthNote_jsx",
            elements: {
              strong: 'strong'
            },
            options: {
              appName
            }
          })
        })
      })]
    }) : null
  });
}
export function AccountTile({
  account
}) {
  return /*#__PURE__*/_jsx(UITile, {
    compact: true,
    children: /*#__PURE__*/_jsx(UITileSection, {
      children: /*#__PURE__*/_jsx(UIMediaObject, {
        itemLeft: /*#__PURE__*/_jsx(UIIconCircle, {
          name: "contacts",
          color: BATTLESHIP,
          backgroundColor: KOALA,
          borderWidth: 0,
          size: 16
        }),
        children: /*#__PURE__*/_jsx("div", {
          className: "justify-between",
          children: /*#__PURE__*/_jsxs("div", {
            children: [/*#__PURE__*/_jsx("div", {
              style: {
                textTransform: 'none'
              },
              children: account.accountName
            }), account.accountIdentifier !== '' && /*#__PURE__*/_jsx("div", {
              children: /*#__PURE__*/_jsx(Small, {
                use: "help",
                children: account.accountIdentifier
              })
            })]
          })
        })
      })
    })
  });
}
export function DataSyncReconnectModal({
  portalId,
  appId,
  account,
  onComplete,
  onCancel
}) {
  return /*#__PURE__*/_jsx(UnifiedAuthView, {
    isModal: true,
    isReauth: true,
    portalId: portalId,
    fetchAuthConfig: fetchAuthConfigForDataSync(portalId, appId, account.id, undefined /* don't override the hublet */),
    onComplete: onComplete,
    onCancel: onCancel,
    introScreen: ({
      appName
    }) => /*#__PURE__*/_jsxs("div", {
      children: [/*#__PURE__*/_jsx("p", {
        children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
          message: "universalAuth.dataSync.reconnectModal.description_jsx",
          options: {
            appName
          },
          elements: {
            strong: 'strong'
          }
        })
      }), /*#__PURE__*/_jsx(AccountTile, {
        account: account
      })]
    })
  });
}
export function DataSyncSuccessDialog(props) {
  const {
    appName,
    onCancel,
    portalId,
    appId,
    authResponse,
    onSetupSyncClick,
    onDoThisLaterClick,
    onOpenExternalLink
  } = props;
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(UIDialogHeaderImage, {
      offsetBottom: -30,
      offsetTop: 59,
      children: /*#__PURE__*/_jsx(UIIllustration, {
        alt: "Illustration of a green checkmark",
        name: "success-green",
        width: 118
      })
    }), /*#__PURE__*/_jsxs(UIDialogHeader, {
      children: [/*#__PURE__*/_jsx(H2, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "universalAuth.modal.successTitle",
          options: {
            appName
          }
        })
      }), /*#__PURE__*/_jsx(UIDialogCloseButton, {
        onClick: onCancel
      })]
    }), /*#__PURE__*/_jsx(DataSyncSuccessScreen, {
      portalId: portalId,
      appId: appId,
      appName: appName,
      account: authResponse,
      onOpenExternalLink: onOpenExternalLink,
      onSetupSyncClick: onSetupSyncClick,
      onDoThisLaterClick: onDoThisLaterClick
    })]
  });
}